import React, { useState, FC } from "react";
import { Button, CircularProgress } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "../../inputs/ByzMuiTypography";

const ADD_ICON_DEFAULT = "/assets/icons/buttons/delete/delete.svg";
const ADD_ICON_HOVER = "/assets/icons/buttons/delete/delete-hovered.svg";

interface ByzMuiDeleteButtonProps extends React.ComponentProps<typeof Button> {
  onClick?: () => void;
  link?: string;
  popup?: boolean;
  loading?: boolean;
}

const StyledButton = styled(Button)<{ popup?: boolean }>(
  ({ popup, disabled, theme }) => ({
    borderRadius: popup ? "20px" : "4px",
    padding: "4px 20px",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    border: `1px solid ${disabled ? theme.palette.action.disabled : theme.palette.primary.main}`,
    backgroundColor: disabled
      ? alpha(theme.palette.action.disabled, 0.2)
      : alpha(theme.palette.primary.main, 0.1),
    color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
    cursor: disabled ? 'not-allowed' : 'pointer',
    "&:hover": {
      backgroundColor: disabled ? undefined : theme.palette.error.main,
      color: disabled ? undefined : theme.palette.common.white,
    },
  })
);

const ByzMuiDeleteButton: FC<ByzMuiDeleteButtonProps> = ({
  onClick,
  link,
  popup,
  disabled,
  children,
  loading,
  ...rest
}) => {
  const { t } = useTranslate();
  const [hover, setHover] = useState(false);

  const btn = (
    <StyledButton
      popup={popup}
      disabled={disabled || loading}
      onClick={loading ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      {loading ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        <img
          src={hover ? ADD_ICON_HOVER : ADD_ICON_DEFAULT}
          alt={t("buttons.delete")}
          style={{ width: "16px", height: "16px", transition: "0.2s ease-in-out" }}
        />
      )}
      <ByzMuiTypography fontWeight={400} variant="body2" sx={{ alignItems: "center", justifyContent: "center" }}>
        {children || t("buttons.delete")}
      </ByzMuiTypography>
    </StyledButton>
  );

  return link ? (
    <Link to={link} style={{ textDecoration: "none" }}>{btn}</Link>
  ) : (
    btn
  );
};

export default ByzMuiDeleteButton;